import {
	Button,
	Create,
	Datagrid,
	DateField,
	DeleteButton,
	Edit,
	FunctionField,
	List,
	SelectInput,
	SimpleForm,
	TextField,
	TextInput,
	useNotify,
	useRecordContext,
	useRefresh,
	useUpdate,
	required
} from 'react-admin';
import { Stack } from '@mui/material';
import { default as icon } from '@mui/icons-material/Security';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const actionChoices = [
	{ id: 'permanent_block', name: 'Permanent block' },
	{ id: 'permanent_allow', name: 'Permanent allow' }
];

function actionLabel( action ) {
	switch ( action ) {
		case 'failed':
			return 'Failed attempts';
		case 'temporary_block':
			return 'Temporary block';
		case 'permanent_block':
			return 'Permanent block';
		case 'permanent_allow':
			return 'Permanent allow';
		default:
			return action;
	}
}

const MakeActionButton = ({ action, label, icon: buttonIcon }) => {
	const record = useRecordContext();
	const notify = useNotify();
	const refresh = useRefresh();
	const [ update, { isLoading } ] = useUpdate();

	if ( !record || record.readOnly || record.action === action ) {
		return null;
	}

	const handleClick = event => {
		event.stopPropagation();
		update(
			'ip-access',
			{
				id: record.id,
				data: { ...record, action },
				previousData: record
			},
			{
				onSuccess: () => {
					notify( 'IP access rule updated' );
					refresh();
				},
				onError: error => notify( error.message || 'Update failed', { type: 'error' } )
			}
		);
	};

	return (
		<Button label={label} onClick={handleClick} disabled={isLoading}>
			{buttonIcon}
		</Button>
	);
};

const RowActions = () => {
	const record = useRecordContext();
	if ( !record || record.readOnly ) {
		return null;
	}

	return (
		<Stack direction="row" spacing={0.5}>
			<MakeActionButton
				action="permanent_block"
				label="Block"
				icon={<BlockIcon />}
			/>
			<MakeActionButton
				action="permanent_allow"
				label="Allow"
				icon={<CheckCircleIcon />}
			/>
			<DeleteButton mutationMode="pessimistic" />
		</Stack>
	);
};

const IPAccessList = () => (
	<List exporter={false}>
		<Datagrid bulkActionButtons={false} rowClick={record => record.readOnly ? false : 'edit'}>
			<TextField source="address" />
			<FunctionField label="Action" render={record => actionLabel( record.action )} />
			<TextField source="source" />
			<TextField source="failed_attempts" label="Failed" />
			<DateField source="window_started_at" showTime />
			<DateField source="blocked_until" showTime />
			<TextField source="note" />
			<RowActions />
		</Datagrid>
	</List>
);

const IPAccessInputs = (
	<>
		<TextInput source="address" validate={required()} helperText="Exact IP or CIDR range, for example 203.0.113.10 or 203.0.113.0/24" />
		<SelectInput source="action" choices={actionChoices} validate={required()} defaultValue="permanent_block" />
		<TextInput source="note" multiline />
	</>
);

const IPAccessEdit = () => (
	<Edit mutationMode="pessimistic" redirect="edit">
		<SimpleForm warnWhenUnsavedChanges>
			{IPAccessInputs}
		</SimpleForm>
	</Edit>
);

const IPAccessCreate = () => (
	<Create>
		<SimpleForm>
			{IPAccessInputs}
		</SimpleForm>
	</Create>
);

export const IPAccess = {
	list: IPAccessList,
	edit: IPAccessEdit,
	create: IPAccessCreate,
	icon
};

export default IPAccess;
